/**
 * Iter 204 PART B — Personnel Hygiene modal (compact create + preview PDF).
 * Reuses PdfPreviewDialog. Backed by /api/personnel/hygiene endpoints.
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Modal, Btn, Input, Textarea, Select, Badge } from "@/components/UI";
import { openPdfPreview } from "@/lib/pdfPreview";
import { ShieldCheck, FileText, Trash2 } from "lucide-react";
import { todayTR } from "@/lib/date";

const HYGIENE_ITEMS = [
  "İş kıyafeti uygunluğu",
  "Saç/sakal koruyucu",
  "El hijyeni",
  "Takı/aksesuar kontrolü",
  "Koruyucu ekipman kullanımı",
  "Genel hijyen uygunluğu",
];

export default function HygieneModal({ employee, open, onClose }) {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState(HYGIENE_ITEMS.map(x => ({ item: x, passed: true, note: "" })));
  const [overall, setOverall] = useState("UYGUN");
  const [notes, setNotes] = useState("");

  const load = async () => {
    if (!employee?.id) return;
    try {
      const r = await api.get(`/personnel/hygiene?personnel_id=${employee.id}`);
      setRows(Array.isArray(r) ? r : []);
    } catch { setRows([]); }
  };
  useEffect(() => { if (open) load(); }, [open, employee?.id]);

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/personnel/hygiene", {
        personnel_id: employee.id,
        check_date: todayTR(),
        items, overall_status: overall, notes,
      });
      await load();
      setItems(HYGIENE_ITEMS.map(x => ({ item: x, passed: true, note: "" })));
      setOverall("UYGUN"); setNotes("");
    } catch (e) { alert(e.response?.data?.detail || "Kaydedilemedi"); }
    finally { setBusy(false); }
  };

  const dlPdf = async (id) => {
    try {
      const blob = await api.getBlob(`/personnel/hygiene/${id}/pdf`);
      openPdfPreview(blob, `Hijyen_${employee?.first_name || ""}_${id.slice(0, 6)}.pdf`);
    } catch (e) { alert(e.response?.data?.detail || "PDF alınamadı"); }
  };

  const del = async (id) => {
    if (!confirm("Hijyen kaydı silinsin mi?")) return;
    await api.delete(`/personnel/hygiene/${id}`);
    load();
  };

  if (!employee) return null;
  return (
    <Modal open={open} onClose={onClose} title={`Hijyen Kontrolleri — ${employee.first_name} ${employee.last_name}`} size="lg">
      <div className="text-xs bg-sky-50 border border-sky-200 rounded p-2 mb-3 text-sky-900" data-testid="hyg-info">
        GMP hijyen izlenebilirliği. Tıbbi tanı/ilaç bilgisi kaydı yapılmaz. Kontrol Eden: <b>{user?.name || user?.username}</b>.
      </div>
      <div className="mb-4 border border-slate-200 rounded p-3 bg-slate-50/50" data-testid="hyg-new">
        <div className="text-xs font-semibold uppercase text-slate-600 mb-2">Yeni Kontrol</div>
        {items.map((it, i) => (
          <div key={i} className="flex items-center gap-2 mb-1.5" data-testid={`hyg-item-${i}`}>
            <div className="flex-1 text-xs">{it.item}</div>
            <Select value={String(it.passed)} onChange={e => { const c = [...items]; c[i] = { ...c[i], passed: e.target.value === "true" }; setItems(c); }}
              className="w-28" data-testid={`hyg-pass-${i}`}>
              <option value="true">Uygun</option><option value="false">Uygun Değil</option>
            </Select>
            <input value={it.note || ""} onChange={e => { const c = [...items]; c[i] = { ...c[i], note: e.target.value }; setItems(c); }}
              placeholder="Not (opsiyonel)" className="w-48 px-2 py-1 text-xs border rounded" />
          </div>
        ))}
        <div className="grid grid-cols-2 gap-3 mt-2">
          <Select label="Genel Sonuç *" value={overall} onChange={e => setOverall(e.target.value)} data-testid="hyg-overall">
            <option value="UYGUN">UYGUN</option>
            <option value="UYGUN_DEGIL">UYGUN DEĞİL</option>
          </Select>
          <Textarea label="Notlar" value={notes} onChange={e => setNotes(e.target.value)} rows={2} data-testid="hyg-notes"/>
        </div>
        <div className="flex justify-end mt-2">
          <Btn onClick={save} disabled={busy} data-testid="hyg-save"><ShieldCheck size={12}/> Kaydet</Btn>
        </div>
      </div>
      <div>
        <div className="text-xs font-semibold uppercase text-slate-600 mb-1">Geçmiş ({rows.length})</div>
        {rows.length === 0 && <div className="text-xs italic text-slate-400 py-2">Kayıt yok.</div>}
        {rows.map(r => (
          <div key={r.id} className="flex items-center justify-between border-b border-slate-100 py-1.5 text-xs" data-testid={`hyg-row-${r.id}`}>
            <div>
              <span className="font-mono">{r.check_date}</span> · <Badge level={r.overall_status === "UYGUN" ? "ok" : "warn"}>{r.overall_status}</Badge>
              <span className="text-slate-500 ml-2">Kontrol: {r.checked_by_name}</span>
            </div>
            <div className="flex gap-1">
              <Btn size="sm" variant="ghost" onClick={() => dlPdf(r.id)} data-testid={`hyg-pdf-${r.id}`}><FileText size={11}/> PDF</Btn>
              <Btn size="sm" variant="danger" onClick={() => del(r.id)} data-testid={`hyg-del-${r.id}`}><Trash2 size={11}/></Btn>
            </div>
          </div>
        ))}
      </div>
    </Modal>
  );
}
